import React, { useState, useEffect, useRef } from 'react';
import styles from './CartesianGraph.module.scss';
import Toggle from '../../inputs/Toggle';

interface ChartData {
  group: string;
  series: { name: string; value: number }[];
}

interface CartesianGraphProps {
  data: ChartData[];
  width?: number;
  height?: number;
  xAxisLegendMargin?: number;
  title?: string;
  subTitle?: string;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  titleFontSize?: number;
  subTitleFontSize?: number;
  xAxisLabelFontSize?: number;
  yAxisLabelFontSize?: number;
  legendFontSize?: number;
  colors?: string[];
  textColor?: string;
  cumul?: boolean;
  grapheType?: 'Bar' | 'Line';
  showLegend?: boolean
  onVisibilityChange?: (visibility: boolean[]) => void;
    onClick?: (event: React.MouseEvent<SVGElement, MouseEvent>, id?: number, state?: boolean) => void;
  onMouseEnter?: (
    event: React.MouseEvent<SVGElement>,
    groupName?: string,
    seriesName?: string,
    value?: number
  ) => void;
  onMouseLeave?: (
    event: React.MouseEvent<SVGElement>,
    groupName?: string,
    seriesName?: string,
    value?: number
  ) => void;
}

const CartesianGraph: React.FC<CartesianGraphProps> = ({
  data = [],
  width = 738,
  height = 316,
  xAxisLegendMargin = 40,
  title = "",
  subTitle = "",
  paddingTop = 50,
  paddingBottom = 50,
  paddingLeft = 50,
  paddingRight = 5,
  titleFontSize = 16,
  subTitleFontSize = 14,
  xAxisLabelFontSize = 12,
  yAxisLabelFontSize = 12,
  legendFontSize = 14,
  colors = [
    "var(--color-2)", // ligne 1
    "var(--color-1)", // ligne 2
    "var(--color-3)", // ligne 3
    "var(--color-4)", // ligne 4
    "var(--color-5)"  // ligne 5
  ],
    textColor =  "var(--color-0)",
  cumul = false,
  grapheType = 'Bar',
  showLegend = true,
  onVisibilityChange,
    onClick,
  onMouseEnter,
  onMouseLeave,
}) => {


  const [, setTooltip] = useState<{ x: number; y: number; value: number; name: string } | null>(null);
  const [dimensions, setDimensions] = useState({ width, height });
  const [availableWidth, setAvailableWidth] = useState(width - paddingLeft - paddingRight);
  const [availableHeight, setAvailableHeight] = useState(height - paddingTop - paddingBottom);
  const [seriesInvisibility, setSeriesInvisibility] = useState<boolean[]>(  data.length > 0 ? data[0].series.map(() => false) : [] );

  const graphRef = useRef<HTMLDivElement>(null);

 useEffect(() => {
	
    const handleResize = () => {
      if (graphRef.current) {
        setDimensions({ width: graphRef.current.offsetWidth, height: graphRef.current.offsetHeight });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (graphRef.current) {
      resizeObserver.observe(graphRef.current);
    }

    handleResize();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

 useEffect(() => {
	 
     setAvailableWidth  ( dimensions.width - paddingLeft - paddingRight );
     setAvailableHeight ( dimensions.height - paddingTop - paddingBottom );
  
  }, [dimensions]);

useEffect(() => {
  if (
    data.length > 0 &&
    (seriesInvisibility.length === 0 ||
     seriesInvisibility.length !== data[0].series.length)
  ) {
    setSeriesInvisibility(data[0].series.map(() => false));
  }
}, [data]);

 const handleVisibilityChange = (index: number) => {
    const newInvisibility = [...seriesInvisibility];
    newInvisibility[index] = !newInvisibility[index];
    setSeriesInvisibility(newInvisibility);
    console.log ("G newInvisibility=", newInvisibility)	
	
    if (onVisibilityChange) {
      onVisibilityChange(newInvisibility);
    }
  };

let maxValue = 5;

if (cumul) {
  maxValue = 0;
  data.forEach((group) => {
    let cumulativeValue = 0;
    group.series.forEach((series, seriesIndex) => {
      // Ajouter la valeur de la série seulement si elle est visible
      if (!seriesInvisibility[seriesIndex]) {
        cumulativeValue += series.value;
      }
    });
    if (cumulativeValue > maxValue) {
      maxValue = cumulativeValue;
    }
  });
} else {
  // Filtrer les valeurs des séries en fonction de leur visibilité
  const visibleSeriesValues = data.flatMap(group =>
    group.series.filter((_, seriesIndex) => !seriesInvisibility[seriesIndex]).map(s => s.value)
  );

  // Calculer maxValue en utilisant uniquement les valeurs des séries visibles
  maxValue = visibleSeriesValues.length > 0 ? Math.max(...visibleSeriesValues) : 0;
}

if (maxValue < 5) {
  maxValue = 5;
}


//  const singleSeriesPerGroup = data.every(group => group.series.length === 1);



  const renderTitles = (): JSX.Element => {
    return (
      <g>
        <g className={styles.textGroup}>
          <text
            x={dimensions.width / 2}
            y={(paddingTop - titleFontSize - subTitleFontSize - 5) / 2 + titleFontSize / 2}
            textAnchor="middle"
            fontSize={`${titleFontSize}px`}
            fontWeight="bold"
            fill={textColor}
          >
            {title}
          </text>
        </g>
        <g className={styles.textGroup}>
          <text
            x={dimensions.width / 2}
            y={(paddingTop - titleFontSize - subTitleFontSize - 5) / 2 + titleFontSize + 5 + subTitleFontSize / 2}
            textAnchor="middle"
            fontSize={`${subTitleFontSize}px`}
            fill={textColor}
          >
            {subTitle}
          </text>
        </g>
      </g>
    );
  };

const renderLegend = (x: number, y: number): JSX.Element => {
  if (!showLegend) {
    return <></>;
  }

  let legendWidth = 110; // Augmenter la largeur pour inclure l'icône
  let legendHeight = 50;
  let legendNames = [];

  if (data.length > 0) {
    for (let i = 0; i < data[0].series.length; i++) {
      legendNames.push(data[0].series[i].name);
    }
    legendHeight = 15 + data[0].series.length * legendFontSize;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={legendWidth}
          height={legendHeight}
          fill="none"
          stroke={textColor}
          strokeWidth="1"
        />
		
{legendNames.map((name, index) => {
  const lineHeight = legendFontSize + 6; // Ajoute un petit espacement
  const centerY = y + index * lineHeight + legendFontSize / 2 + 5;

  return (
    <g key={index} className={styles.textGroup}>
      <rect
        x={x + 5}
        y={centerY - legendFontSize / 2}
        width={legendFontSize - 3}
        height={legendFontSize - 3}
        fill={colors[index]}
        stroke="none"
        strokeWidth="1"
      />
      <text
        x={x + 20}
        y={centerY}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={`${legendFontSize}px`}
        fontWeight="bold"
        fill={textColor}
      >
        {name}
      </text>
      <foreignObject
        x={x + legendWidth - 33}
        y={centerY - 12.5} // Toggle height = 25px
        width="30"
        height="25"
      >
        <Toggle
          height="25px"
          checked={!seriesInvisibility[index]}
          onChange={() => handleVisibilityChange(index)}
          form="switch"
        />
      </foreignObject>
    </g>
  );
})}

      </g>
    );
  } else {
    return <></>;
  }
};

  const renderAxes = (): JSX.Element => {
    return (
      <g>
        <line
          x1={paddingLeft - 2}
          y1={dimensions.height - paddingBottom}
          x2={dimensions.width - paddingRight}
          y2={dimensions.height - paddingBottom}
          stroke={textColor}
          strokeWidth="1"
        />
        <line
          x1={paddingLeft - 2}
          y1={paddingTop}
          x2={paddingLeft - 2}
          y2={dimensions.height - paddingBottom}
          stroke={textColor}
          strokeWidth="1"
        />
      </g>
    );
  };

  const renderYTicks = (): JSX.Element => {
    const yTicks = Array.from({ length: Math.ceil(maxValue) }, (_, i) => i + 1);
    const yTickSpacing = availableHeight / yTicks.length;
    const ySkipValues = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

    let yLabelSkip = 1;
    for (const skip of ySkipValues) {
      if (yTickSpacing * skip < yAxisLabelFontSize * 1.5) {
        yLabelSkip = skip;
      } else {
        break;
      }
    }

    return (
      <g>
        {yTicks.map((tick, index) => {
          if (yLabelSkip > 1 && tick % yLabelSkip !== 0) return null;

          const y = dimensions.height - paddingBottom - (tick / maxValue) * availableHeight;
          return (
            <g key={index} className={styles.textGroup}>
              <line
                x1={paddingLeft - 2}
                y1={y}
                x2={dimensions.width - paddingRight}
                y2={y}
                stroke={textColor}
                strokeWidth="0.5"
              />
              <text
                x={paddingLeft - 10}
                y={y + 5}
                textAnchor="end"
                fill={textColor}
                fontSize={`${yAxisLabelFontSize}px`}
              >
                {tick}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  const renderXTicks = (): JSX.Element => {
	  
    const xTickSpacing = data.length > 0 ? availableWidth / data.length : availableWidth;
    // Largeur estimée de l'étiquette la plus longue (~0,6 em par caractère, plus
    // une marge) : un seuil fixe en « 5 caractères » laissait les dates longues
    // se superposer à l'horizontale.
    const longestLabel = data.reduce((max, d) => Math.max(max, String(d.group ?? '').length), 0);
    const canLabelsBeStraight = xTickSpacing >= xAxisLabelFontSize * Math.max(5, longestLabel * 0.6 + 1);

    let xLabelSkip = 1;
	
    if ( canLabelsBeStraight === false ) 
	 {
      for (let skip = 1; skip < data.length; skip++) 
	   {
        xLabelSkip = skip;

        if ( xTickSpacing * skip > xAxisLabelFontSize * 1.5) 
		 {
          break;
         }
       }
     }

    return (
      <g>
        {data.map((group, index) => {
			
          if (index % xLabelSkip !== 0) {
            return null;
          }

          const groupX = paddingLeft + index * xTickSpacing + (xTickSpacing * 0.8) / 2;
          const textY = dimensions.height - paddingBottom + 15;

          return (
            <React.Fragment key={index}>
              <g className={styles.textGroup}>
                <text
                  x={groupX}
                  y={textY}
                  textAnchor={canLabelsBeStraight ? 'middle' : 'end'}
                  fill={textColor}
                  fontSize={`${xAxisLabelFontSize}px`}
                  className={styles.text}
                  transform={canLabelsBeStraight ? '' : `rotate(-45 ${groupX} ${textY})`}
                >
                  {group.group}
                </text>
              </g>
              {!canLabelsBeStraight && xLabelSkip > 1 && (
                <line
                  x1={groupX}
                  y1={textY - 5}
                  x2={groupX}
                  y2={dimensions.height - paddingBottom}
                  stroke={textColor}
                  strokeWidth="0.5"
                />
              )}
            </React.Fragment>
          );
        })}
      </g>
    );
  };

  const renderNoData = (): JSX.Element => {
    return (
      <g key={`no-data`} className={styles.textGroup}>
        <text
          x={paddingLeft + (availableWidth / data.length * 0.8) / 2}
          y={dimensions.height - paddingBottom + 15}
          textAnchor="middle"
          fontSize={`${xAxisLabelFontSize}px`}
          fill="var(--color-R)"
          className={styles.text}
        >
          Aucune donnée
        </text>
      </g>
    );
  };

  const renderDatas = (): JSX.Element => {
    switch (grapheType) {
      case 'Bar':
        return renderDatasWithBar();
      case 'Line':
        return renderDatasWithLine();
      default:
        return <></>;
    }
  };



const renderDatasWithLine = (): JSX.Element => {
  return (
    <>
      {data.map((group, groupIndex) => {
        if (group.series.length === 0) {
          return renderNoData();
        }
        const seriesPoints = group.series.map((series, seriesIndex) => {
          if (seriesInvisibility[seriesIndex]) {
            return null;
          }
          const points = data.map((group, groupIndex) => {
            const x = paddingLeft + groupIndex * (availableWidth / data.length) + (availableWidth / data.length * 0.8) / 2;
            let yValue = 0;
            if (cumul) {
              let cumulativeValue = 0;
              for (let i = 0; i <= seriesIndex; i++) {
                if (!seriesInvisibility[i]) {
                  const seriesData = group.series[i];
                  cumulativeValue += seriesData ? seriesData.value : 0;
                }
              }
              yValue = (cumulativeValue / maxValue) * availableHeight;
            } else {
              const seriesData = group.series[seriesIndex];
              yValue = seriesData ? (seriesData.value / maxValue) * availableHeight : 0;
            }
            const y = dimensions.height - paddingBottom - yValue;
            return { x, y };
          });
          return points;
        });

        const paths = seriesPoints.map((points, seriesIndex) => {
          if (!points) {
            return null;
          }
          const pathData = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
          return (
            <g key={`series-${seriesIndex}`}>
              <path
                d={pathData}
                fill="none"
                stroke={colors[seriesIndex % colors.length]}
                strokeWidth="1"
                className={styles.line}
              />
              {points.map((point, i) => (
                <circle
                  key={`dot-${seriesIndex}-${i}`}
                  cx={point.x}
                  cy={point.y}
                  r={4}
                  fill={colors[seriesIndex % colors.length]}
                  stroke={textColor}
                  strokeWidth="1"
                  onClick={(e) => onClick?.(e, i, seriesInvisibility[seriesIndex])}
                  onMouseEnter={(e) => onMouseEnter?.(e, group.group, group.series[seriesIndex].name, group.series[seriesIndex].value)}
                  onMouseLeave={(e) => onMouseLeave?.(e, group.group, group.series[seriesIndex].name, group.series[seriesIndex].value)}
                />
              ))}
            </g>
          );
        }).filter(Boolean);

        return <g key={`group-${groupIndex}`}>{paths}</g>;
      })}
    </>
  );
};



const renderDatasWithBar = (): JSX.Element => {
  const visibleSeriesCount = data.length > 0 ? data[0].series.filter((_, seriesIndex) => !seriesInvisibility[seriesIndex]).length : 0;
  const T = data.length > 0 ? availableWidth / data.length : availableWidth;
  const k = 0.1;
  const b = T / (visibleSeriesCount + (visibleSeriesCount + 3) * k);
  const e = k * b;

  return (
    <>
      {data.map((group, groupIndex) => {
        if (group.series.length === 0) {
          return renderNoData();
        }
        const bars = group.series.map((series, seriesIndex) => {
          if (seriesInvisibility[seriesIndex]) {
            return null;
          }
          const seriesData = group.series[seriesIndex];
          let x, y, yValue, height;
          if (cumul) {
            let cumulativeValue = 0;
            for (let i = 0; i <= seriesIndex; i++) {
              if (!seriesInvisibility[i]) {
                const seriesData = group.series[i];
                cumulativeValue += seriesData ? seriesData.value : 0;
              }
            }
            yValue = (cumulativeValue / maxValue) * availableHeight;
            height = seriesData ? (seriesData.value / maxValue) * availableHeight : 0;
            x = paddingLeft + groupIndex * T;
            y = dimensions.height - paddingBottom - yValue;
          } else {
            const visibleIndex = group.series.slice(0, seriesIndex + 1).filter((_, index) => !seriesInvisibility[index]).length - 1;
            x = paddingLeft + groupIndex * T + (visibleIndex + 1) * e + visibleIndex * b;
            yValue = seriesData ? (seriesData.value / maxValue) * availableHeight : 0;
            y = dimensions.height - paddingBottom - yValue;
            height = yValue;
          }
          return (
            <rect
              key={`bar-${groupIndex}-${seriesIndex}`}
              x={x}
              y={y}
              width={cumul ? T : b}
              height={height}
              fill={colors[seriesIndex % colors.length]}
              className={styles.bar}
              onClick={(e) => onClick?.(e, groupIndex, seriesInvisibility[seriesIndex])}
              onMouseEnter={(e) => onMouseEnter?.(e, group.group, series.name, series.value)}
              onMouseLeave={(e) => onMouseLeave?.(e, group.group, series.name, series.value)}
            />
          );
        }).filter(Boolean);
        return <g key={`group-${groupIndex}`}>{bars}</g>;
      })}
    </>
  );
};


  return (
    <div ref={graphRef} style={{ width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        width="100%"
        height="100%"
        aria-labelledby={title}
        role="img"
        onMouseLeave={() => setTooltip(null)}
      >
        {renderTitles()}
        {renderLegend(dimensions.width - 125, 5)}
        {renderAxes()}
        {renderYTicks()}
        {renderXTicks()}
        {renderDatas()}
      </svg>
    </div>
  );
};

export default CartesianGraph;
